import React, {
  Fragment,
  useState,
  useEffect,
  useReducer,
  useCallback,
} from "react";
import * as R from "ramda";
import { Affix, DatePicker, Empty, Icon, Input, Select, Spin, Tag } from "antd";

import {
  uploadResourceChannelMap,
  UploadResourceChannelToTextMap,
} from "./video-resource-page";
import {
  // getResourceList,
  transformOssCoverUrl,
  transformResourceItemToFullPath,
  listAliyunResourcesApiWithMediaInfo,
} from "../aliyun-service-api";
import {
  BatchButtonGroup,
  useAddDownloadSignal,
} from "../widgets/batch-button-group";
import { getTypeIcon } from "../compose-util";
import { BaseTypeCarousel } from "./base-type";
import { TagSelect } from "../widgets/resource-tag-modal";
import { AdvanceSearchForm } from "../widgets/search-form";
import { ChannelSelect } from "../widgets/form-item/channel-select";
import { FilterRowController } from "../widgets/filter-row-controller";
import { detailIsVideo, ListItem, ListItemDiv } from "../widgets/list-item";
import { ResourceTypeSelect } from "../widgets/form-item/resource-type-select";

const defaultPagination = {
  current: 0,
  //  这儿设置不能超过 20, 因为在获取数据时, 会同步获取阿里云的数据, 阿里云的数据每次查询最多20条
  pageSize: 20,
  total: 0,
};

export const debounce = (fn, delay) => {
  let timer = null;
  return function () {
    const context = this;
    const args = arguments;
    clearTimeout(timer);
    timer = setTimeout(() => {
      fn.apply(context, args);
    }, delay);
  };
};

const formValuesToParams = (values) => {
  return R.pipe(
    R.toPairs,
    R.map(([key, value]) => {
      //  如果value是undefined, 则返回[key, undefined]
      if (value === undefined) return [key, value];

      //  如果value是数组, 且长度为0, 则返回[key, undefined]
      if (Array.isArray(value) && value.length === 0) return [key, undefined];

      //  时间范围
      if (key === "created_at") {
        //  to seconds instead of milliseconds
        const begin_time = value[0].unix();
        const end_time = value[1].unix();
        return [
          ["begin_time", begin_time],
          ["end_time", end_time],
        ];
      }

      //  其他情况
      if (Array.isArray(value)) {
        return [key, value.join(",")];
      }

      //  其他情况
      return [key, value];
    }),
    R.flatten,
    R.splitEvery(2),
    //  omit undefined values
    R.filter(([, value]) => value !== undefined),
    R.fromPairs
  )(values);
};

export const Channel = ({ value, detail }) => {
  if (!detailIsVideo(detail)) {
    //  audio, text, img
    return getTypeIcon(detail.type);
  }
  // noinspection JSNonASCIINames
  const channelMap = {
    // [OldVideoChannelMap["直播剪辑"]]: { color: "blue", text: "直播剪辑" },
    //  直播切片
    [uploadResourceChannelMap["直播切片"]]: { color: "cyan", text: "直播切片" },
    [uploadResourceChannelMap["本地手动上传"]]: {
      color: "blue",
      text: "本地视频(手动)",
    },
    [uploadResourceChannelMap["本地自动上传"]]: {
      color: "blue",
      text: "本地视频(自动)",
    },
    //  合成素材
    [uploadResourceChannelMap["合成素材"]]: {
      color: "purple",
      text: "合成素材",
    },
    //  图传视频素材(APP)
    [uploadResourceChannelMap["图传视频素材(APP)"]]: {
      color: "green",
      text:
        UploadResourceChannelToTextMap[
          uploadResourceChannelMap["图传视频素材(APP)"]
        ],
    },
  };
  return channelMap[value] ? (
    <Fragment>
      {getTypeIcon(detail.type)}
      <Tag style={{zoom: 0.7}} color={channelMap[value].color}>{channelMap[value].text}</Tag>
    </Fragment>
  ) : null;
};

const initialState = {
  dataSource: [],
  pagination: defaultPagination,
  selectedKeys: [],
  loading: false,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "SET_DATA":
      const { items, total } = action.payload;
      const transformedList = items.map(
        R.pipe(transformResourceItemToFullPath, transformOssCoverUrl)
      );
      return {
        ...state,
        dataSource:
          state.pagination.current === 0
            ? transformedList
            : [...state.dataSource, ...transformedList],
        pagination: {
          ...state.pagination,
          total,
        },
      };
    case "RESET":
      return initialState;
    case "SET_SELECTED_KEYS":
      return {
        ...state,
        selectedKeys: action.payload,
      };
    case "TOGGLE_LOADING":
      return {
        ...state,
        loading: !state.loading,
      };
    case "SHOW_LOADING":
      return {
        ...state,
        loading: true,
      };
    case "HIDE_LOADING":
      return {
        ...state,
        loading: false,
      };
    case "BATCH_HIDDEN":
      const { hidden } = action.payload;
      return {
        ...state,
        dataSource: state.dataSource.map((item) =>
          state.selectedKeys.includes(item.id) ? { ...item, hidden } : item
        ),
      };
    case "BATCH_DEL":
      const ids = action.payload;
      return {
        ...state,
        dataSource: state.dataSource.filter((item) => !ids.includes(item.id)),
      };
    case "SET_PAGE":
      return {
        ...state,
        pagination: {
          ...state.pagination,
          current: action.payload,
        },
      };
    case "UPDATE_ONE":
      const { id } = action.payload;
      return {
        ...state,
        dataSource: state.dataSource.map((item) =>
          item.id === id ? { ...item, ...action.payload } : item
        ),
      };
    default:
      return state;
  }
};

export const VideoResourceTab = (props) => {
  const { orderId, availableTags = [], resetIndex = 0 } = props;
  const extraParams = R.pathOr({}, ["extraParams"], props);
  const [state, dispatch] = useReducer(reducer, initialState);
  const refreshTagList = R.pathOr(R.T, ["refreshTagList"], props);
  const [previewIndex, setPreviewIndex] = useState(-1);

  const [filterParams, setFilterParams] = useState([]);

  const loadMoreData = useCallback(() => {
    dispatch({ type: "TOGGLE_LOADING" });
    //  listAliyunResourcesApiWithMediaInfo
    listAliyunResourcesApiWithMediaInfo({
      ...extraParams,
      orderId,
      page: state.pagination.current + 1,
      page_size: state.pagination.pageSize,
      ...filterParams,
    })
      .then((res) => {
        dispatch({ type: "SET_DATA", payload: res });
        dispatch({ type: "SET_PAGE", payload: state.pagination.current + 1 });
      })
      .finally(() => {
        dispatch({ type: "TOGGLE_LOADING" });
      });
  }, [JSON.stringify(extraParams), state.pagination.current, filterParams]);

  useEffect(() => {
    dispatch({ type: "RESET" });
  }, [JSON.stringify(extraParams), JSON.stringify(filterParams), resetIndex]);

  useEffect(() => {
    if (state.pagination.current === 0) {
      loadMoreData();
    }
  }, [state.pagination.current]);

  const batchHidden = useCallback(
    (ids, hidden) => {
      dispatch({ type: "BATCH_HIDDEN", payload: { ids, hidden } });
    },
    [JSON.stringify(extraParams), filterParams]
  );

  const batchDel = useCallback(
    (ids) => {
      dispatch({ type: "BATCH_DEL", payload: ids });
    },
    [JSON.stringify(extraParams), filterParams]
  );

  const debouncedOnChange = debounce((values) => {
    const processedParams = formValuesToParams(values);
    setFilterParams(processedParams);
  }, 300); // 300ms debounce delay

  const { getIsDownloaded, refreshKey, addDownloadTag } = useAddDownloadSignal(
    orderId
  );

  return (
    <Fragment>
      <Spin spinning={state.loading}>
        <div className="mb-4">
          <div className="p-2">
            <FilterRowController
              orderId={orderId}
              refreshTagList={refreshTagList}
              refreshList={() => dispatch({ type: "RESET" })}
              {...props}
            >
              <AdvanceSearchForm onChange={debouncedOnChange}>
                <ResourceTypeSelect
                  label="类型"
                  name="type"
                  allowClear
                  mode="multiple"
                  maxTagCount={1}
                />
                <Select label="状态" name="hidden" allowClear>
                  <Select.Option value={1}>隐藏</Select.Option>
                  <Select.Option value={0}>公开</Select.Option>
                </Select>
                <ChannelSelect
                  label="来源"
                  name="channel"
                  allowClear
                  mode="multiple"
                  maxTagCount={1}
                />
                <TagSelect
                  label="标签"
                  name="tag_id"
                  tagList={availableTags}
                  allowClear
                  orderId={orderId}
                  mode="multiple"
                  maxTagCount={1}
                  refreshTagList={refreshTagList}
                />
                <Input label="名称" name="name" allowClear />
                <Input label="描述" name="desc" allowClear />
                <DatePicker.RangePicker
                  label="时间"
                  name="created_at"
                  allowClear
                  style={{ width: "100%" }}
                />
              </AdvanceSearchForm>
            </FilterRowController>
            <div className="mt-4 border-bottom-gray" />
          </div>
          <Affix
            offsetTop={0}
            className="antd-affix"
            target={() => document.querySelector("#mainWrapper")}
          >
            <div className="p-2">
              <BatchButtonGroup
                {...props}
                addDownloadTag={addDownloadTag}
                batchHidden={batchHidden}
                orderId={orderId}
                list={state.dataSource}
                selectedKeys={state.selectedKeys}
                setSelectedKeys={(selectedKeys) =>
                  dispatch({ type: "SET_SELECTED_KEYS", payload: selectedKeys })
                }
                batchDel={(ids) => {
                  batchDel(ids);
                  dispatch({ type: "SET_SELECTED_KEYS", payload: [] });
                }}
                rowKey="id"
              />
            </div>
          </Affix>
          <div
            className="flex-wrap p-2"
            key={JSON.stringify(state.dataSource) + refreshKey}
          >
            {state.dataSource.length > 0 ? (
              state.dataSource.map((detail) => (
                <ListItem
                  availableTags={availableTags}
                  onPreview={() => {
                    const indexOfDetail = state.dataSource.findIndex(
                      (item) => item.id === detail.id
                    );
                    setPreviewIndex(indexOfDetail);
                  }}
                  detail={detail}
                  isDownloaded={getIsDownloaded(detail.id)}
                  selectedKeys={state.selectedKeys}
                  setSelectedKeys={(selectedKeys) =>
                    dispatch({
                      type: "SET_SELECTED_KEYS",
                      payload: selectedKeys,
                    })
                  }
                  rowKey="id"
                  showLoading={() => dispatch({ type: "SHOW_LOADING" })}
                  hideLoading={() => dispatch({ type: "HIDE_LOADING" })}
                  updateOne={(newDetail) => {
                    dispatch({
                      type: "UPDATE_ONE",
                      payload: R.pipe(
                        transformResourceItemToFullPath,
                        transformOssCoverUrl
                      )(newDetail),
                    });
                  }}
                  orderId={orderId}
                />
              ))
            ) : (
              <div className="flex-y-center flex-x-center h-full justify-center w-full p-4">
                {state.loading ? <Icon type="loading" /> : <Empty />}
              </div>
            )}
            {state.pagination.current * state.pagination.pageSize <
              state.pagination.total && (
              <ListItemDiv>
                <div className="flex-y-center flex-x-center h-full justify-center w-full p-4">
                  <a
                    onClick={() => {
                      dispatch({
                        type: "SET_PAGE",
                        payload: state.pagination.current + 1,
                      });
                      loadMoreData();
                    }}
                  >
                    加载第{" "}
                    {state.pagination.current * state.pagination.pageSize + 1} ~{" "}
                    {Math.min(
                      state.pagination.current * state.pagination.pageSize +
                        state.pagination.pageSize,
                      state.pagination.total
                    )}{" "}
                    / {state.pagination.total} 条数据
                  </a>
                </div>
              </ListItemDiv>
            )}
          </div>
        </div>
      </Spin>
      <BaseTypeCarousel
        current={previewIndex}
        dataSource={state.dataSource}
        setPreviewIndex={setPreviewIndex}
        loading={state.loading}
        updateOne={(newDetail) => {
          dispatch({
            type: "UPDATE_ONE",
            payload: R.pipe(
              transformResourceItemToFullPath,
              transformOssCoverUrl
            )(newDetail),
          });
        }}
        {...props}
      />
    </Fragment>
  );
};



// WEBPACK FOOTER //
// ./src/containers/Video/tab-pages/video-resource-tab.js